import { Box, Link, MenuItem, Select, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { ApiQueryAutocomplete } from "../../../../../../core/api/components/ApiQueryAutocomplete"
import { LabeledField } from "../../../../../../shared/components/forms/LabeledField"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import type { DtDateRange } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"

import {
    ID_CARD_STATUS_FILTERS,
    ID_CARD_STATUS_L10N_BASE,
    ID_CARD_TYPE_L10N_BASE,
    ID_CARD_TYPES,
} from "../idCardsListHelpers"
import { IdCardStatus, IdCardStatusFilterOption } from "../idCardsListTypes"
import type {
    IdCardCardholderOption,
    IdCardCompanyOption,
    IdCardsListFilters as Filters,
    IdCardsListView,
    IdCardStatusFilter,
} from "../idCardsListTypes"

const ALL_CARD_TYPES = "ALL"

const STATUS_DOT_COLORS: Readonly<Record<IdCardStatusFilter, string>> = {
    [IdCardStatusFilterOption.ALL]: tailwindPalette.slate[400],
    [IdCardStatusFilterOption.GUEST]: tailwindPalette.indigo[500],
    [IdCardStatus.ASSIGNED]: tailwindPalette.green[500],
    [IdCardStatus.UNASSIGNED]: tailwindPalette.sky[500],
    [IdCardStatus.EXPIRED]: tailwindPalette.amber[500],
    [IdCardStatus.INACTIVE]: tailwindPalette.red[500],
}

const useStyles = makeStyles()((theme) => ({
    filters: {
        "&&": {
            flexWrap: "wrap",
        },
    },
    control: {
        width: "13rem",
        "& .MuiOutlinedInput-root": {
            borderRadius: ".75rem",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
        },
    },
    optionMeta: {
        color: theme.palette.tailwind.slate[500],
        whiteSpace: "nowrap",
    },
    summary: {
        padding: "0 1rem 1rem",
        color: theme.palette.tailwind.slate[600],
    },
}))

const useDotStyles = makeStyles<Readonly<{ color: string }>>()((_theme, { color }) => ({
    dot: {
        width: ".5rem",
        height: ".5rem",
        borderRadius: "50%",
        flexShrink: 0,
        backgroundColor: color,
    },
}))

function StatusOptionContent({ status, count }: Readonly<{ status: IdCardStatusFilter; count: number | undefined }>) {
    const { classes } = useStyles()
    const { classes: dotClasses } = useDotStyles({ color: STATUS_DOT_COLORS[status] })

    return (
        <Stack direction="row" alignItems="center" gap=".5rem" width="100%">
            <span className={dotClasses.dot} />
            <LocalizedEnum base={ID_CARD_STATUS_L10N_BASE} enumValue={status}>
                <span>{status}</span>
            </LocalizedEnum>
            <Box marginLeft="auto" className={classes.optionMeta}>
                {count}
            </Box>
        </Stack>
    )
}

type IdCardsListFiltersProps = Readonly<{
    view: IdCardsListView | undefined
    isLoading: boolean
    isError: boolean
    filters: Filters
    search: string | undefined
    period: DtDateRange
    onFiltersChange: (filters: Filters) => void
    onPeriodChange: (period: DtDateRange) => void
    onClear: () => void
}>

export function IdCardsListFilters({
    view,
    isLoading,
    isError,
    filters,
    search,
    period,
    onFiltersChange,
    onPeriodChange,
    onClear,
}: IdCardsListFiltersProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const getStatusCount = (status: IdCardStatusFilter) =>
        view?.statusOptions.find((option) => option.value === status)?.count

    const summary = [
        filters.status !== IdCardStatusFilterOption.ALL &&
            l10n.getString("merchant-id-cards-list-filter-summary-status", {
                value: l10n.getStringForEnum(ID_CARD_STATUS_L10N_BASE, filters.status, filters.status),
            }),
        view?.selectedCompany &&
            l10n.getString("merchant-id-cards-list-filter-summary-company", { value: view.selectedCompany.name }),
        view?.selectedCardholder &&
            l10n.getString("merchant-id-cards-list-filter-summary-cardholder", {
                value: view.selectedCardholder.name,
            }),
        filters.cardType &&
            l10n.getString("merchant-id-cards-list-filter-summary-card-type", {
                value: l10n.getStringForEnum(ID_CARD_TYPE_L10N_BASE, filters.cardType, filters.cardType),
            }),
        search?.trim() && l10n.getString("merchant-id-cards-list-filter-summary-search", { value: search.trim() }),
    ]
        .filter(Boolean)
        .join("  ·  ")

    return (
        <Stack>
            <StandardTableFilter variant="left" stackClassName={classes.filters}>
                <StandardDateRangeFilter value={period} onChange={onPeriodChange} variant="labeled" />

                <LocalizedStrict id="merchant-id-cards-list-filter-status-select-label" attrs={{ label: true }}>
                    <LabeledField label="" bold={false} gap=".25rem" className={classes.control}>
                        <Select
                            fullWidth
                            value={filters.status}
                            SelectDisplayProps={{ "aria-label": l10n.getString("merchant-id-cards-table-status") }}
                            onChange={(e) => {
                                const status = ID_CARD_STATUS_FILTERS.find((value) => value === e.target.value)
                                if (status) onFiltersChange({ ...filters, status })
                            }}
                            renderValue={(status) => (
                                <StatusOptionContent status={status} count={getStatusCount(status)} />
                            )}
                        >
                            {ID_CARD_STATUS_FILTERS.map((status) => (
                                <MenuItem key={status} value={status}>
                                    <StatusOptionContent status={status} count={getStatusCount(status)} />
                                </MenuItem>
                            ))}
                        </Select>
                    </LabeledField>
                </LocalizedStrict>

                <LocalizedStrict id="merchant-id-cards-list-filter-company" attrs={{ label: true }}>
                    <LabeledField label="" bold={false} gap=".25rem" className={classes.control}>
                        <LocalizedStrict id="merchant-id-cards-list-filter-company" attrs={{ placeholder: true }}>
                            <ApiQueryAutocomplete<IdCardCompanyOption>
                                fullWidth
                                isLoading={isLoading}
                                isError={isError}
                                options={view?.companyOptions ?? []}
                                value={view?.selectedCompany ?? null}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_e, option) =>
                                    onFiltersChange({ ...filters, companyId: option?.id, cardholder: undefined })
                                }
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Stack direction="row" justifyContent="space-between" gap=".5rem" width="100%">
                                            <span>{option.name}</span>
                                            <span className={classes.optionMeta}>{option.count}</span>
                                        </Stack>
                                    </Box>
                                )}
                            />
                        </LocalizedStrict>
                    </LabeledField>
                </LocalizedStrict>

                <LocalizedStrict id="merchant-id-cards-list-filter-cardholder" attrs={{ label: true }}>
                    <LabeledField label="" bold={false} gap=".25rem" className={classes.control}>
                        <LocalizedStrict id="merchant-id-cards-list-filter-cardholder" attrs={{ placeholder: true }}>
                            <ApiQueryAutocomplete<IdCardCardholderOption>
                                fullWidth
                                isLoading={isLoading}
                                isError={isError}
                                options={view?.cardholderOptions ?? []}
                                value={view?.selectedCardholder ?? null}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                onChange={(_e, option) => onFiltersChange({ ...filters, cardholder: option?.value })}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Stack width="100%">
                                            <span>{option.name}</span>
                                            <Typography variant="caption" className={classes.optionMeta}>
                                                {[
                                                    option.companyName,
                                                    l10n.getString("merchant-id-cards-list-filter-cards-count", {
                                                        count: option.count,
                                                    }),
                                                ]
                                                    .filter(Boolean)
                                                    .join(" · ")}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                )}
                            />
                        </LocalizedStrict>
                    </LabeledField>
                </LocalizedStrict>

                <LocalizedStrict id="merchant-id-cards-table-card-type-select-label" attrs={{ label: true }}>
                    <LabeledField label="" bold={false} gap=".25rem" className={classes.control}>
                        <Select
                            fullWidth
                            value={filters.cardType ?? ALL_CARD_TYPES}
                            SelectDisplayProps={{ "aria-label": l10n.getString("merchant-id-cards-table-card-type") }}
                            onChange={(e) =>
                                onFiltersChange({
                                    ...filters,
                                    cardType: ID_CARD_TYPES.find((value) => value === e.target.value),
                                })
                            }
                        >
                            <MenuItem value={ALL_CARD_TYPES}>
                                <LocalizedEnum base={ID_CARD_TYPE_L10N_BASE} enumValue={ALL_CARD_TYPES}>
                                    <span>{ALL_CARD_TYPES}</span>
                                </LocalizedEnum>
                            </MenuItem>
                            {ID_CARD_TYPES.map((value) => (
                                <MenuItem key={value} value={value}>
                                    <LocalizedEnum base={ID_CARD_TYPE_L10N_BASE} enumValue={value}>
                                        <span>{value}</span>
                                    </LocalizedEnum>
                                </MenuItem>
                            ))}
                        </Select>
                    </LabeledField>
                </LocalizedStrict>
            </StandardTableFilter>

            {view?.isFiltering && (
                <Stack direction="row" flexWrap="wrap" alignItems="center" gap=".75rem" className={classes.summary}>
                    <Typography variant="body2">{summary}</Typography>
                    <LocalizedStrict id="merchant-id-cards-list-filter-clear-button">
                        <Link component="button" variant="body2" onClick={onClear}>
                            Clear filters
                        </Link>
                    </LocalizedStrict>
                </Stack>
            )}
        </Stack>
    )
}
